import type {
    AddressMode,
    HalContext,
    HalContextDescriptor,
    HalContextStats,
    HalGeometry,
    HalGeometryRef,
    HalImage,
    HalImagePhysical,
    HalImageRef,
    HalImageVirtual,
    HalImageVirtualRef,
    HalPaintable,
    HalPainterImageBlit,
    HalPainterImageBlitRef,
    HalPainterImageCompositor,
    HalPainterImageCompositorRef,
    HalPainterPrimitive,
    HalPainterPrimitiveRef,
    PartialHalImageDescriptor,
    ShaderReturnType,
} from "@cm/hal"
import {HalRef} from "@cm/hal"
import type {Box2Like, ColorLike, Vector2Like} from "@cm/math"
import {Box2, Vector2} from "@cm/math"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"
import {WebGl2ImageCachePhysical} from "#webgl2/webgl2-image-cache-physical"
import {WebGl2ImageCacheVirtual} from "#webgl2/webgl2-image-cache-virtual"
import {WebGl2GeometryCache} from "#webgl2/webgl2-geometry-cache"
import {WebGl2PainterPrimitive} from "#webgl2/webgl2-painter-primitive"
import {WebGl2PainterImageBlit} from "#webgl2/webgl2-painter-image-blit"
import type {WebGl2ImageVirtualPageTableUpdates} from "#webgl2/webgl2-image-virtual-page-table-updates"
import {WebGl2PainterImageCompositor} from "#webgl2/webgl2-painter-image-compositor"
import {WebGl2ImageVirtual} from "#webgl2/webgl2-image-virtual"
import {WebGl2ImageBase} from "#webgl2/webgl2-image-base"
import {WebGl2ImageView} from "#webgl2/webgl2-image-view"
import {WebGl2Geometry} from "#webgl2/webgl2-geometry"
import {WebGl2ImagePhysical} from "#webgl2/webgl2-image-physical"
import {WebGl2ShaderCache} from "#webgl2/webgl2-shader-cache"
import {WebGl2GeometryVertexAttribute} from "#webgl2/webgl2-geometry-vertex-attribute"
import {loggerFactory} from "@cm/browser-log"
import {isFirefox} from "@cm/browser-utils"

export type WebGl2CreateArgs = {
    physicalCache: {
        sizeInBytes: number
    }
    virtualCache?: {
        sizeInBytes: number
        numAtlasPages: number
        maxNumAtlases: number
    }
}

export class WebGl2Context implements HalContext {
    readonly descriptor: HalContextDescriptor = {
        atlasPageSize: WebGl2ImageAtlas.pageSize,
    }

    readonly maxDrawBuffers: number
    readonly maxRenderBufferSize: number
    readonly maxTextureUnits: number
    readonly maxTextureSize: number
    readonly maxTextureLayers: number
    readonly EXT_color_buffer_float: boolean
    readonly EXT_color_buffer_half_float: boolean
    readonly EXT_float_blend: boolean

    static createFromCanvas(canvas: HTMLCanvasElement, args: WebGl2CreateArgs): WebGl2Context {
        logger.log("Initializing WebGL2 canvas")
        if (!canvas) {
            throw Error("WebGL canvas is null.")
        }
        const gl = canvas.getContext("webgl2", {
            antialias: false,
            preserveDrawingBuffer: false,
            alpha: true,
            premultipliedAlpha: true,
        })
        if (!gl) {
            throw Error("WebGL2 context could not be obtained from canvas.")
        }
        return WebGl2Context.createFromWebGL2RenderingContext(gl, args)
    }

    static createFromWebGL2RenderingContext(gl: WebGL2RenderingContext, args: WebGl2CreateArgs): WebGl2Context {
        return new WebGl2Context(gl, args)
    }

    private constructor(gl: WebGL2RenderingContext, args: WebGl2CreateArgs) {
        this._gl = gl
        gl.disable(gl.DEPTH_TEST)
        this.maxDrawBuffers = gl.getParameter(gl.MAX_DRAW_BUFFERS)
        this.maxTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS)
        this.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE)
        this.maxTextureLayers = gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS)
        this.maxRenderBufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)
        if (isFirefox) {
            this.maxTextureSize = Math.min(this.maxTextureSize, 8192) // interestingly firefox on win returns 16384, but only seems to handle 8192 properly, so we artificially restrict it here (checked 31.3.23)
        }
        this.EXT_color_buffer_float = gl.getExtension("EXT_color_buffer_float") != null
        this.EXT_color_buffer_half_float = gl.getExtension("EXT_color_buffer_half_float") != null
        this.EXT_float_blend = gl.getExtension("EXT_float_blend") != null
        logger.log("WebGL2 context initialized.")
        logger.log("  MaxDrawBuffers: ", this.maxDrawBuffers)
        logger.log("  maxTextureUnits: ", this.maxTextureUnits)
        logger.log("  MaxTextureSize: ", this.maxTextureSize)
        logger.log("  MaxTextureLayers: ", this.maxTextureLayers)
        logger.log("  MaxRenderBufferSize: ", this.maxRenderBufferSize)
        logger.log("  EXT_color_buffer_float: ", this.EXT_color_buffer_float ? "yes" : "no")
        logger.log("  EXT_color_buffer_half_float: ", this.EXT_color_buffer_half_float ? "yes" : "no")
        logger.log("  EXT_float_blend: ", this.EXT_float_blend ? "yes" : "no")

        logger.trace("Creating caches")
        this._caches = {
            shaderCache: new WebGl2ShaderCache(this),
            imageCachePhysical: new WebGl2ImageCachePhysical(this, args.physicalCache.sizeInBytes, true),
            imageCacheVirtual: args.virtualCache
                ? new WebGl2ImageCacheVirtual(this, args.virtualCache.sizeInBytes, args.virtualCache.numAtlasPages, args.virtualCache.maxNumAtlases)
                : null,
            geometryCache: new WebGl2GeometryCache(this),
        }
        logger.trace("Creating standard geometries")
        const unitRectGeometry = this.createGeometry()
        unitRectGeometry.ref.addRect(Box2.fromPositionAndSize(Vector2.zero, Vector2.one), Box2.fromPositionAndSize(Vector2.zero, Vector2.one))
        this._geometries = {
            unitRectGeometry,
        }
        logger.trace("Creating standard utilities")
        logger.trace("  - Blitter")
        const blitter = this.createHalPainterImageBlit()
        logger.trace("  - FillPainter")
        const fillPainter = this.createHalPainterImageCompositor(`
            uniform vec4 u_color;

            vec4 computeColor(ivec2 texelIndex) {
                return u_color;
            }`)
        logger.trace("  - CopyToAtlasPainter")
        const copyToAtlasPainter = this.createHalPainterPrimitive(`
            vec4 computeColor(vec2 position, vec2 uv, vec4 color) {
                int channel = int(round(color.r));
                int mipLevel = int(round(color.g));
                float value = texelChannelFetchLod0(ivec2(uv), mipLevel, channel, ADDRESS_MODE_BORDER, 0.0);
                return vec4(value, 0, 0, 1);
            }`)
        logger.trace("  - UpdatePageTablePainter")
        const updatePageTablePainter = this.createHalPainterPrimitive(
            `
            vec4 computeColor(vec2 position, vec2 uv, vec4 color) {
                return vec4(color.r, 0.0, 0.0, 0.0);
            }`,
        )
        logger.trace("  - PropagateCoarserMipLevelsToMissingPagesCompositor")
        const propagateCoarserMipLevelsToMissingPagesCompositor = this.createHalPainterImageCompositor(
            `
            uniform int u_pageTableWidth;

            vec4 computeColor(ivec2 texelIndex) {
                int pageLookupWidth = u_pageTableWidth;
                int numChannels = u_numChannels[0];
                int numMipLevels = u_numMipLevels[0];
                int fullLinearTexelIndex = texelIndex.y * pageLookupWidth + texelIndex.x;
                int linearTexelIndex = fullLinearTexelIndex / numChannels;
                int targetTexelChannel = fullLinearTexelIndex % numChannels;

                // find the mip level for this texel index
                int targetTexelMipLevel = numMipLevels - 1;
                ivec2 targetTexelPageIndex;
                for (; targetTexelMipLevel >= 0; --targetTexelMipLevel) {
                    int firstPageIndex;
                    ivec2 numMipLevelPages;
                    getPageLookupMipLevelInfo(0, targetTexelMipLevel, firstPageIndex, numMipLevelPages);
                    int relativePageIndex = linearTexelIndex - firstPageIndex;
                    if (relativePageIndex >= 0 && relativePageIndex < numMipLevelPages.x * numMipLevelPages.y) {
                        targetTexelPageIndex = ivec2(relativePageIndex % numMipLevelPages.x, relativePageIndex / numMipLevelPages.x);
                        break;
                    }
                }

                // propagate from coarser mip levels until we find a valid page
                ivec2 pageIndex = targetTexelPageIndex;
                int mipLevel = targetTexelMipLevel;
                uvec4 targetPageTableData = texelFetchPageTable0(pageIndex, targetTexelChannel, mipLevel);
                uint coarsenedLevels = 0u;
                for (; mipLevel < numMipLevels;) {
                    if (targetPageTableData.w != 0u && targetPageTableData.z == 0u) {
                        break;  // we found a valid page
                    }
                    pageIndex >>= 1;
                    mipLevel++;
                    targetPageTableData = texelFetchPageTable0(pageIndex, targetTexelChannel, mipLevel);
                    coarsenedLevels++;
                }

                // Read raw packed float from found page, update only coarsenedLevels bits (19-16)
                ivec2 foundPageTableTexelIndex = computePageTableTexelIndex(0, pageIndex, targetTexelChannel, mipLevel);
                ivec3 foundPhysicalIndex = computePhysicalIndex(0, foundPageTableTexelIndex, 0);
                uint rawPacked = floatBitsToUint(texelFetch(u_image0, foundPhysicalIndex, 0).r);
                rawPacked = (rawPacked & 0xFFF0FFFFu) | (coarsenedLevels << 16u);
                return vec4(uintBitsToFloat(rawPacked), 0.0, 0.0, 0.0);
            }`,
        )
        logger.trace("  - CopyPageInfoCompositor")
        const copyPageInfo = this.createHalPainterImageCompositor(
            `
            vec4 computeColor(ivec2 texelIndex) {
                return vec4(texelFetch0(texelIndex).r, 0.0, 0.0, 0.0);
            }
            `,
        )
        this._utilities = {
            blitter,
            fillPainter,
            copyToAtlasPainter,
            updatePageTablePainter,
            propagateCoarserMipLevelsToMissingPagesCompositor,
            copyPageInfo,
        }
    }

    // HalEntity
    get context(): HalContext {
        return this
    }

    // HalEntity
    dispose() {
        if (!this._gl) {
            throw new Error("WebGL2 context already disposed")
        }
        // unbind everything
        const gl = this._gl
        for (let unit = 0; unit < this.maxTextureUnits; ++unit) {
            gl.activeTexture(gl.TEXTURE0 + unit)
            gl.bindTexture(gl.TEXTURE_2D, null)
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, null)
            gl.bindTexture(gl.TEXTURE_3D, null)
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, null)
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, null)
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)
        gl.bindRenderbuffer(gl.RENDERBUFFER, null)
        gl.bindFramebuffer(gl.FRAMEBUFFER, null)
        // dispose
        if (this._utilities) {
            this._utilities.blitter.release()
            this._utilities.fillPainter.release()
            this._utilities.propagateCoarserMipLevelsToMissingPagesCompositor.release()
            this._utilities.copyToAtlasPainter.release()
            this._utilities.copyPageInfo.release()
            this._utilities.updatePageTablePainter.release()
            this._utilities = null
        }
        if (this._geometries) {
            this._geometries.unitRectGeometry.release()
            this._geometries = null
        }
        if (this._caches) {
            this._caches.shaderCache.dispose()
            this._caches.imageCachePhysical.dispose()
            if (this._caches.imageCacheVirtual) {
                this._caches.imageCacheVirtual.dispose()
                this._caches.imageCacheVirtual.disposeAtlases()
            }
            this._caches.geometryCache.dispose()
            this._caches = null
        }
        // explicitly lose context to free all resources
        const ext = this._gl.getExtension("WEBGL_lose_context")
        if (ext) {
            ext.loseContext()
        }
        this._gl = null
        logger.log("WebGL2 context disposed")

        if (WebGl2ImagePhysical.instances && WebGl2ImagePhysical.instances.size > 0) {
            logger.warn(`WebGl2CanvasComponent: There are still WebGl2ImagePhysical instances alive.`, WebGl2ImagePhysical.instances)
        }
        if (WebGl2PainterPrimitive.instances && WebGl2PainterPrimitive.instances.size > 0) {
            logger.warn(`WebGl2CanvasComponent: There are still WebGl2PainterPrimitive instances alive.`, WebGl2PainterPrimitive.instances)
        }
        if (WebGl2PainterImageCompositor.instances && WebGl2PainterImageCompositor.instances.size > 0) {
            logger.warn(`WebGl2CanvasComponent: There are still WebGl2PainterImageCompositor instances alive.`, WebGl2PainterImageCompositor.instances)
        }
        if (WebGl2PainterImageBlit.instances && WebGl2PainterImageBlit.instances.size > 0) {
            logger.warn(`WebGl2CanvasComponent: There are still WebGl2PainterImageBlit instances alive.`, WebGl2PainterImageBlit.instances)
        }
        if (WebGl2GeometryVertexAttribute.instances && WebGl2GeometryVertexAttribute.instances.size > 0) {
            logger.warn(`WebGl2CanvasComponent: There are still WebGl2GeometryVertexAttribute instances alive.`, WebGl2GeometryVertexAttribute.instances)
        }
    }

    get gl(): WebGL2RenderingContext {
        if (!this._gl) {
            throw new Error("WebGL2 context is disposed")
        }
        return this._gl
    }

    // HalContext
    get stats(): HalContextStats {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        return {
            caches: {
                physical: this._caches.imageCachePhysical.stats,
                virtual: this._caches.imageCacheVirtual
                    ? {
                          ...this._caches.imageCacheVirtual.stats,
                          atlasStats: this._caches.imageCacheVirtual.atlases.map((a) => a.stats),
                      }
                    : undefined,
            },
        }
    }

    // TODO remove these
    createHalPainterPrimitiveLegacy(shadingFunction: string, returnType?: ShaderReturnType): HalPainterPrimitive {
        return new WebGl2PainterPrimitive(this, shadingFunction, returnType)
    }

    createHalPainterImageCompositorLegacy(compositingFunction?: string, returnType?: ShaderReturnType): HalPainterImageCompositor {
        return new WebGl2PainterImageCompositor(this, compositingFunction, returnType)
    }

    createHalPainterImageBlitLegacy(compositingFunction?: string): HalPainterImageBlit {
        return new WebGl2PainterImageBlit(this, compositingFunction)
    }

    createHalGeometryLegacy(): HalGeometry {
        return new WebGl2Geometry(this)
    }

    createHalImageLegacy(descriptor: PartialHalImageDescriptor): HalImagePhysical {
        return new WebGl2ImagePhysical(this, descriptor)
    }

    createHalImageVirtualLegacy(descriptor: PartialHalImageDescriptor): HalImageVirtual {
        return new WebGl2ImageVirtual(this, descriptor)
    }

    createShader(shadingFunction: string, returnType?: ShaderReturnType, conservativeRasterization?: boolean) {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        return this._caches.shaderCache.getOrCreateShader(shadingFunction, returnType, conservativeRasterization)
    }

    // HalContext
    createHalPainterPrimitive(shadingFunction: string, returnType?: ShaderReturnType): HalPainterPrimitiveRef {
        const painter = new WebGl2PainterPrimitive(this, shadingFunction, returnType)
        return new HalRef(painter, (painter) => painter.dispose())
    }

    // HalContext
    createHalPainterImageCompositor(compositingFunction?: string, returnType?: ShaderReturnType): HalPainterImageCompositorRef {
        const painter = new WebGl2PainterImageCompositor(this, compositingFunction, returnType)
        return new HalRef(painter, (painter) => painter.dispose())
    }

    // HalContext
    createHalPainterImageBlit(compositingFunction?: string, returnType?: ShaderReturnType): HalPainterImageBlitRef {
        const painter = new WebGl2PainterImageBlit(this, compositingFunction, returnType)
        return new HalRef(painter, (painter) => painter.dispose())
    }

    // HalContext
    createPhysicalImage(descriptor: PartialHalImageDescriptor): HalImageRef {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        return this._caches.imageCachePhysical.getImagePtr(descriptor)
    }

    // HalContext
    createVirtualImage(descriptor: PartialHalImageDescriptor, pagePriority?: number): HalImageVirtualRef {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        if (!this._caches.imageCacheVirtual) {
            throw new Error("Virtual image cache is not initialized")
        }
        const image = this._caches.imageCacheVirtual.getImagePtr(descriptor)
        if (image.ref instanceof WebGl2ImageVirtual) {
            image.ref.setPagePriority(pagePriority ?? DEFAULT_PAGE_PRIORITY)
            return image as HalImageVirtualRef
        } else {
            throw new Error("Internal error: requested virtual image is not a WebGl2ImageVirtual")
        }
    }

    // HalContext
    createGeometry(): HalGeometryRef {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        return this._caches.geometryCache.getGeometry()
    }

    getUnitRectGeometry(): HalGeometryRef {
        if (!this._geometries) {
            throw new Error("WebGL2 context is disposed")
        }
        return this._geometries.unitRectGeometry.addRef()
    }

    // HalContext
    async flush(waitForCompletion: boolean): Promise<void> {
        const gl = this.gl
        let sync: WebGLSync | null = null
        if (waitForCompletion) {
            sync = gl.fenceSync(this.gl.SYNC_GPU_COMMANDS_COMPLETE, 0)
            if (!sync) {
                throw new Error("Failed to create sync object")
            }
        }
        gl.flush()
        if (sync) {
            const clientWaitAsync = (gl: WebGL2RenderingContext, sync: WebGLSync, flags: number, interval_ms: number) => {
                return new Promise<void>((resolve, reject) => {
                    function testForGlCompletion() {
                        const res = gl.clientWaitSync(sync, flags, 0)
                        if (res == gl.WAIT_FAILED) {
                            reject()
                            return
                        }
                        if (res == gl.TIMEOUT_EXPIRED) {
                            setTimeout(testForGlCompletion, interval_ms)
                            return
                        }
                        resolve()
                    }

                    testForGlCompletion()
                })
            }
            await clientWaitAsync(gl, sync, 0, 0)
            gl.deleteSync(sync)
        }
    }

    // HalContext
    blit(args: {
        sourceImage: HalImage
        sourceRegion?: Box2Like
        sourceMipLevel?: number
        targetImage: HalPaintable
        targetOffset?: Vector2Like
        targetMipLevel?: number
        addressMode?: AddressMode
    }) {
        if (!this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        this._utilities.blitter.ref.paint({
            target: args.targetImage,
            sourceImages: args.sourceImage,
            options: {
                sourceRegion: args.sourceRegion,
                sourceMipLevel: args.sourceMipLevel,
                targetOffset: args.targetOffset,
                targetMipLevel: args.targetMipLevel,
                addressMode: args.addressMode,
            },
        })
    }

    // HalContext
    fill(args: {target: HalPaintable; targetRegion?: Box2Like; targetMipLevel?: number; color: ColorLike}) {
        if (!this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        this._utilities.fillPainter.ref.paint({
            target: args.target,
            options: {
                targetRegion: args.targetRegion,
                targetMipLevel: args.targetMipLevel,
            },
            parameters: {
                u_color: {type: "float4", value: {x: args.color.r, y: args.color.g, z: args.color.b, w: args.color.a ?? 1}},
            },
        })
    }

    findAtlasForAllocation(count: number): WebGl2ImageAtlas {
        if (!this._caches?.imageCacheVirtual) {
            throw new Error("Virtual cache not initialized")
        }
        return this._caches.imageCacheVirtual.findAtlasForAllocation(count)
    }

    getAtlasById(id: number): WebGl2ImageAtlas {
        if (!this._caches?.imageCacheVirtual) {
            throw new Error("Virtual cache not initialized")
        }
        return this._caches.imageCacheVirtual.getAtlasById(id)
    }

    get atlases(): WebGl2ImageAtlas[] {
        return this._caches?.imageCacheVirtual?.atlases ?? []
    }

    get atlasImageDescriptor() {
        return this._caches?.imageCacheVirtual?.atlasImageDescriptor ?? null
    }

    // HalContext
    freeAllUnusedImages(): void {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        this._caches.imageCachePhysical.freeAllUnusedImages()
        this._caches.imageCacheVirtual?.freeAllUnusedImages()
    }

    // HalContext
    listImages(allocatedOnly: boolean): void {
        if (!this._caches) {
            throw new Error("WebGL2 context is disposed")
        }
        logger.log("Physical images:")
        this._caches.imageCachePhysical.listImages(allocatedOnly)
        if (this._caches.imageCacheVirtual) {
            logger.log("Virtual images:")
            this._caches.imageCacheVirtual.listImages(allocatedOnly)
        }
    }

    propagateCoarserMipLevelsToMissingPages(image: WebGl2ImageVirtual, maxSourceMipLevel: number) {
        if (!this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        if (maxSourceMipLevel === 0) {
            return
        }

        // const start = performance.now()
        // console.log("Propagating coarser mip levels to missing pages starting from mip-map " + maxSourceMipLevel)

        image.pageManager.flushPageTableUpdates() // make sure page table is up to date before we start reading from it
        const buffer = this.createPhysicalImage({
            width: image.pageTableTexture.descriptor.width,
            height: image.pageTableTexture.descriptor.height,
            numLayers: 1,
            channelLayout: "R",
            dataType: "float32",
        })
        this._utilities.propagateCoarserMipLevelsToMissingPagesCompositor.ref.paint({
            target: buffer.ref,
            sourceImages: image, // we bind image to have access to the page table sampling functions
            parameters: {
                u_pageTableWidth: {type: "int", value: image.mipmapInfo.pageLookupSize.width},
            },
        })
        this._utilities.copyPageInfo.ref.paint({
            target: image.pageTableTexture,
            sourceImages: buffer.ref,
        })
        buffer.release()

        // console.log("Propagating coarser mip levels to missing pages took " + (performance.now() - start) + "ms")
    }

    copyToAtlas(sourceImage: HalImage, geometry: HalGeometry, atlas: WebGl2ImageAtlas) {
        if (!this._caches || !this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        if (!this._caches.imageCacheVirtual) {
            throw new Error("Atlas not initialized")
        }
        this._utilities.copyToAtlasPainter.ref.paint({
            target: atlas.image,
            sourceImages: [sourceImage],
            geometry: geometry,
        })
    }

    copyFromAtlas(targetImage: HalImage, geometry: HalGeometry, atlas: WebGl2ImageAtlas) {
        if (!this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        if (!this._caches?.imageCacheVirtual) {
            throw new Error("Atlas not initialized")
        }
        this._utilities.copyToAtlasPainter.ref.paint({
            target: targetImage,
            sourceImages: [atlas.image],
            geometry: geometry,
        })
    }

    updatePageTable(pageTableTexture: HalImagePhysical, pageTableUpdates: WebGl2ImageVirtualPageTableUpdates) {
        if (!this._utilities) {
            throw new Error("WebGL2 context is disposed")
        }
        const geometry = pageTableUpdates.flushToGeometry()
        // console.log("Updating page table for mip level " + mipLevel + " and channel " + channel)
        this._utilities.updatePageTablePainter.ref.paint({
            target: pageTableTexture,
            geometry: geometry.ref,
        })
    }

    private _gl: WebGL2RenderingContext | null
    private _caches: Caches | null
    private _geometries: Geometries | null
    private _utilities: Utilities | null
}

type Caches = {
    shaderCache: WebGl2ShaderCache
    imageCachePhysical: WebGl2ImageCachePhysical
    imageCacheVirtual: WebGl2ImageCacheVirtual | null
    geometryCache: WebGl2GeometryCache
}

type Geometries = {
    unitRectGeometry: HalGeometryRef
}

type Utilities = {
    blitter: HalPainterImageBlitRef
    fillPainter: HalPainterImageCompositorRef
    copyToAtlasPainter: HalPainterPrimitiveRef
    updatePageTablePainter: HalPainterPrimitiveRef
    propagateCoarserMipLevelsToMissingPagesCompositor: HalPainterImageCompositorRef
    copyPageInfo: HalPainterImageCompositorRef
}

WebGl2ImageBase.viewFactory = (base, args) => new WebGl2ImageView(base, args)

const DEFAULT_PAGE_PRIORITY = 1

const logger = loggerFactory.getLogger("webgl2")
